/**
 * FloatingAIToggleButton Component
 * Modern floating AI button with enhanced visual effects
 * Positioned at bottom right to show/hide AI Search Panel
 * Mobile only — on desktop the Search button lives in the header next to the menu
 */
import React from 'react';
import { Box, Fab, Typography, Zoom, useMediaQuery, useTheme } from '@mui/material';

import { useAISearchPanel } from '@hooks/useAISearchPanel';
import { AIAssistantIcon } from '@components/chatbot/copilot/AIAssistantIcon';

export const FloatingAIToggleButton: React.FC = () => {
  const { isVisible, showPanel } = useAISearchPanel();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Desktop uses the header Search button
  if (!isMobile) {
    return null;
  }

  return (
    <Zoom
      in={!isVisible}
      timeout={400}
      style={{
        transitionDelay: !isVisible ? '100ms' : '0ms',
      }}
      unmountOnExit
    >
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 20, sm: 24, md: 28 },
          right: { xs: 20, sm: 24, md: 28 },
          zIndex: 1200,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
        }}
      >
        <Fab
          variant="extended"
          onClick={showPanel}
          aria-label="Open AI Assistant"
          sx={{
            height: { xs: 48, sm: 52 },
            borderRadius: '999px',
            backgroundColor: '#fff',
            color: '#374151',
            textTransform: 'none',
            px: { xs: 2, sm: 2.5 },
            gap: 1,
            boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: '#fff',
              boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.16)',
              transform: 'scale(1.02)',
            },
          }}
        >
          <AIAssistantIcon size={22} sx={{ fontSize: { xs: 20, sm: 22 } }} />
          <Typography
            component="span"
            sx={{
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              fontWeight: 600,
              color: '#374151',
              letterSpacing: '-0.01em',
              whiteSpace: 'nowrap',
            }}
          >
            Search
          </Typography>
        </Fab>
      </Box>
    </Zoom>
  );
};

export default FloatingAIToggleButton;
